import { useState, useCallback } from "react";
import {
  verifyDocumentWithFallback,
  isOcrDocumentAccepted,
  getOcrVerdictSummary,
  isImageValidationProfile,
  isCvProfile,
} from "../utils/ocrApi";

export default function useDocumentOcr(
  documentType,
  expectedName,
  {
    cvEducationQuery = "",
    cvExperienceQuery = "",
    expectedBank = "",
    expectedAccount = "",
    jobPosition = "",
    expectedNik = "",
  } = {},
) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const reset = useCallback(() => {
    setLoading(false);
    setResult(null);
    setError(null);
  }, []);

  const verify = useCallback(
    async (file, options = {}) => {
      setLoading(true);
      setError(null);
      setResult(null);

      try {
        const outcome = await verifyDocumentWithFallback(
          file,
          documentType,
          expectedName,
          {
            cvEducationQuery,
            cvExperienceQuery,
            expectedBank,
            expectedAccount,
            jobPosition,
            expectedNik,
            ...options,
          },
        );

        if (outcome.data) {
          setResult(outcome.data);
        }

        if (outcome.skipped) {
          return outcome;
        }

        if (!outcome.ok && outcome.rejectionMessage) {
          setError(outcome.rejectionMessage);
        }

        return outcome;
      } catch {
        return {
          ok: false,
          skipped: true,
          data: null,
          source: "skipped",
          error: null,
          rejectionMessage: null,
        };
      } finally {
        setLoading(false);
      }
    },
    [documentType, expectedName, cvEducationQuery, cvExperienceQuery, expectedBank, expectedAccount, jobPosition, expectedNik],
  );

  return {
    loading,
    result,
    error,
    verify,
    reset,
    accepted: result
      ? isOcrDocumentAccepted(
          result,
          expectedName,
          expectedAccount,
          documentType,
          expectedBank,
        )
      : null,
    summary: result
      ? getOcrVerdictSummary(
          result,
          expectedName,
          expectedAccount,
          documentType,
          expectedBank,
        )
      : null,
    isImageValidation: isImageValidationProfile(documentType),
    isCvValidation: isCvProfile(documentType),
  };
}
